<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
  import { ChevronDown } from 'lucide-vue-next';
  import {
    DIRECTORY_MODULE_ICONS,
    getDirectoryModuleIcon,
    getDirectoryModuleIconLabel,
    isDirectoryIconId,
  } from '@/shared/ui/module-icons';
  import {
    calculateDirectoryIconPickerPanelPosition,
    PANEL_MAX_HEIGHT_PX,
  } from '../model/directoryIconPickerPosition';

  const GRID_COLUMNS = 8;
  const PANEL_LISTBOX_ID = 'directory-icon-picker-listbox';

  const props = defineProps<{
    modelValue: string;
    disabled?: boolean;
    autoOpenWhenEmpty?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
  }>();

  const isOpen = ref(false);
  const triggerRef = ref<HTMLButtonElement | null>(null);
  const panelRef = ref<HTMLElement | null>(null);
  const focusedIndex = ref(0);
  const panelPosition = ref({ top: 0, left: 0, width: 0, maxHeight: `${PANEL_MAX_HEIGHT_PX}px` });

  const selectedIcon = computed(() => getDirectoryModuleIcon(props.modelValue));

  const hasSelection = computed(() => isDirectoryIconId(props.modelValue));

  const triggerLabel = computed(() => {
    if (hasSelection.value) {
      return `Ícone selecionado: ${getDirectoryModuleIconLabel(props.modelValue)}`;
    }
    return 'Selecionar ícone do módulo';
  });

  const getTriggerElement = () => triggerRef.value;

  const getPanelElement = () => panelRef.value;

  const getSelectedIndex = () => {
    const index = DIRECTORY_MODULE_ICONS.findIndex((entry) => entry.id === props.modelValue);
    return index >= 0 ? index : 0;
  };

  const focusOption = (index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, DIRECTORY_MODULE_ICONS.length - 1));
    focusedIndex.value = clampedIndex;

    schedulePanelFocus(clampedIndex);
  };

  const updatePanelPosition = () => {
    const trigger = getTriggerElement();
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const panel = getPanelElement();
    const renderedPanelHeight = panel?.scrollHeight ?? PANEL_MAX_HEIGHT_PX;

    const position = calculateDirectoryIconPickerPanelPosition(
      rect,
      viewportWidth,
      viewportHeight,
      renderedPanelHeight
    );

    panelPosition.value = {
      top: position.top,
      left: position.left,
      width: position.width,
      maxHeight: `${position.maxHeight}px`,
    };
  };

  const syncScrollResizeListeners = (open: boolean) => {
    if (open) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return;
    }
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleResize);
  };

  const removeDocumentListeners = () => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleDocumentKeydown);
  };

  const syncOutsideListener = (open: boolean) => {
    removeDocumentListeners();
    if (!open) {
      return;
    }
    window.setTimeout(() => {
      if (isOpen.value) {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleDocumentKeydown);
      }
    }, 0);
  };

  const closePanel = (returnFocus = true) => {
    if (!isOpen.value) {
      return;
    }
    isOpen.value = false;
    syncOutsideListener(false);
    syncScrollResizeListeners(false);
    if (returnFocus) {
      scheduleTriggerFocus();
    }
  };

  const openPanel = (focusOption = true) => {
    isOpen.value = true;
    focusedIndex.value = getSelectedIndex();
    syncOutsideListener(true);
    syncScrollResizeListeners(true);

    schedulePanelSetup(focusOption);
  };

  const schedulePanelFocus = async (index: number) => {
    await nextTick();
    const panel = getPanelElement();
    const buttons = panel?.querySelectorAll<HTMLButtonElement>('[role="option"]');
    buttons?.[index]?.focus();
  };

  const scheduleTriggerFocus = async () => {
    await nextTick();
    triggerRef.value?.focus();
  };

  const schedulePanelSetup = async (focusOption = true) => {
    await nextTick();
    updatePanelPosition();
    if (focusOption) {
      await schedulePanelFocus(focusedIndex.value);
    }
  };

  /**
   * AC11: um módulo sem ícone configurado precisa exibir o painel de seleção assim que é
   * selecionado. O foco não é movido para não roubar o cursor de quem está digitando o título.
   * O painel não reabre depois de fechado — o pai reinicia este estado remontando o componente
   * (`:key` do nó selecionado).
   */
  onMounted(() => {
    if (props.autoOpenWhenEmpty && !hasSelection.value && !props.disabled) {
      openPanel(false);
    }
  });

  const togglePanel = () => {
    if (props.disabled) {
      return;
    }
    if (isOpen.value) {
      closePanel();
      return;
    }
    openPanel();
  };

  const selectIcon = (id: string) => {
    emit('update:modelValue', id);
    closePanel();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!isOpen.value) {
      return;
    }
    const target = event.target as Node;
    const trigger = getTriggerElement();
    const panel = getPanelElement();
    if (trigger?.contains(target) || panel?.contains(target)) {
      return;
    }
    closePanel(false);
  };

  const handleDocumentKeydown = (event: KeyboardEvent) => {
    if (!isOpen.value) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
    }
  };

  const moveFocusedIndex = (delta: number) => {
    focusOption(focusedIndex.value + delta);
  };

  const handleTriggerKeydown = (event: KeyboardEvent) => {
    if (props.disabled) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isOpen.value) {
        openPanel();
      }
      return;
    }

    if (event.key === 'ArrowUp' && !isOpen.value) {
      event.preventDefault();
      openPanel();
    }
  };

  const handleOptionKeydown = (event: KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveFocusedIndex(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocusedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocusedIndex(GRID_COLUMNS);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocusedIndex(-GRID_COLUMNS);
        break;
      case 'Home':
        event.preventDefault();
        focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        focusOption(DIRECTORY_MODULE_ICONS.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectIcon(DIRECTORY_MODULE_ICONS[index]?.id ?? props.modelValue);
        break;
      case 'Escape':
        event.preventDefault();
        closePanel();
        break;
      default:
        break;
    }
  };

  let isScrollTicking = false;

  const handleScroll = () => {
    if (!isOpen.value || isScrollTicking) {
      return;
    }
    isScrollTicking = true;
    window.requestAnimationFrame(() => {
      updatePanelPosition();
      isScrollTicking = false;
    });
  };

  const handleResize = () => {
    if (isOpen.value) {
      updatePanelPosition();
    }
  };

  watch(isOpen, async (open) => {
    if (open) {
      await nextTick();
      updatePanelPosition();
    }
  });

  onUnmounted(() => {
    removeDocumentListeners();
    syncScrollResizeListeners(false);
  });

  defineExpose({ togglePanel, selectIcon });
</script>

<template>
  <div
    class="relative w-full"
    data-testid="directory-icon-picker"
  >
    <button
      ref="triggerRef"
      type="button"
      role="combobox"
      data-testid="directory-icon-picker-trigger"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-controls="PANEL_LISTBOX_ID"
      :aria-label="triggerLabel"
      :disabled="disabled"
      class="flex w-full items-center rounded-md border border-gray-02 bg-white-01 px-4 py-3 text-left text-gray-07 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      @click.stop="togglePanel"
      @keydown="handleTriggerKeydown"
    >
      <span
        v-if="hasSelection"
        class="flex flex-1 items-center gap-2 truncate"
      >
        <component
          :is="selectedIcon"
          class="size-4 shrink-0 text-gray-07"
          aria-hidden="true"
        />
      </span>
      <span
        v-else
        class="flex-1 truncate text-gray-03"
      >Ícone</span>
      <ChevronDown
        class="pointer-events-none size-4 shrink-0 text-gray-05 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        aria-hidden="true"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="PANEL_LISTBOX_ID"
        ref="panelRef"
        role="listbox"
        data-testid="directory-icon-picker-panel"
        class="overflow-y-auto rounded-2xl border border-gray-02 bg-white-01 p-3 shadow-lg"
        :style="{
          position: 'fixed',
          top: `${panelPosition.top}px`,
          left: `${panelPosition.left}px`,
          width: `${panelPosition.width}px`,
          maxHeight: panelPosition.maxHeight,
          zIndex: 9999,
        }"
      >
        <div class="grid grid-cols-8 gap-1">
          <button
            v-for="(entry, index) in DIRECTORY_MODULE_ICONS"
            :key="entry.id"
            type="button"
            role="option"
            :tabindex="focusedIndex === index ? 0 : -1"
            class="flex size-8 items-center justify-center rounded border border-transparent text-gray-07 hover:bg-gray-02 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-secondary-ink)]"
            :class="{
              'border-[var(--theme-secondary-ink)] bg-gray-02': modelValue === entry.id,
            }"
            :aria-selected="modelValue === entry.id"
            :aria-label="`Selecionar ícone ${getDirectoryModuleIconLabel(entry.id)}`"
            @click.stop="selectIcon(entry.id)"
            @keydown="handleOptionKeydown($event, index)"
          >
            <component
              :is="entry.icon"
              class="size-4"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>
